/**
 * PurchasesKpiBar
 *
 * Four-card KPI strip rendered above the purchase-requests table.
 * Cards:
 *  1. Committed Cost  — Posted PRs not yet invoiced, with cost-budget progress bar
 *  2. Invoiced / Actual Spend — commitment-relieved PRs (fully paid), outstanding PO sub-line
 *  3. Awaiting Approval  — in-flight count + value, oldest request date sub-line
 *  4. Rejected / Cancelled — count + value, most-recent rejection reference sub-line
 */

import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TrendingUp, Receipt, Clock, XCircle } from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import type { PurchaseRequest } from '@/types/purchase-request';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PurchasesKpiBarProps {
  /** All purchase requests for the project (unfiltered). */
  requests: PurchaseRequest[];
  /**
   * Total cost budget for the project (from CostBudget.totals.budget).
   * When undefined / 0 the progress bar and projected marker are hidden.
   */
  costBudgetTotal?: number;
}

// ─── Accent palette ───────────────────────────────────────────────────────────
// Uses the same design-system tokens as CompactKpiCard in kpi-card.tsx so the
// cards are visually identical to the rest of the app's KPI strip style.

const ACCENT = {
  // teal  → maps to "success" design-system tokens
  teal: {
    border:    'border-l-4 border-l-success-500',
    iconBg:    'bg-success-100',
    iconText:  'text-success-600',
    valueText: 'text-success-700',
  },
  // indigo → maps to "info" design-system tokens
  indigo: {
    border:    'border-l-4 border-l-info-500',
    iconBg:    'bg-info-100',
    iconText:  'text-info-600',
    valueText: 'text-foreground',
  },
  // amber  → maps to "warning" design-system tokens
  amber: {
    border:    'border-l-4 border-l-warning-500',
    iconBg:    'bg-warning-100',
    iconText:  'text-warning-600',
    valueText: 'text-warning-700',
  },
  // red    → maps to "error / destructive" design-system tokens
  red: {
    border:    'border-l-4 border-l-destructive',
    iconBg:    'bg-destructive/10',
    iconText:  'text-destructive',
    valueText: 'text-destructive',
  },
} as const;

type AccentKey = keyof typeof ACCENT;

// ─── Inner card shell ─────────────────────────────────────────────────────────

interface KpiShellProps {
  accent:      AccentKey;
  icon:        React.ElementType;
  title:       string;
  value:       React.ReactNode;
  description: React.ReactNode;
  children?:   React.ReactNode;
  className?:  string;
}

function KpiShell({
  accent,
  icon: Icon,
  title,
  value,
  description,
  children,
  className,
}: KpiShellProps) {
  const a = ACCENT[accent];
  return (
    // Layout mirrors CompactKpiCard with accentVariant: icon sits on the right,
    // title / value / description stack on the left.
    <Card className={cn('rounded-lg overflow-hidden', a.border, className)}>
      <CardContent className="flex items-center justify-between px-4 py-3">
        {/* Left: text stack */}
        <div className="space-y-0.5 min-w-0 flex-1 pr-3">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className={cn('text-xl font-bold', a.valueText)}>{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {/* Optional extra content (e.g., progress bar) */}
          {children}
        </div>
        {/* Right: icon badge */}
        <div className={cn('rounded-md p-2 shrink-0', a.iconBg)}>
          <Icon className={cn('h-4 w-4', a.iconText)} />
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Progress bar helpers ─────────────────────────────────────────────────────

/** Returns the CSS repeating-gradient string for the dashed projected marker.
 *  Colours match the design-system destructive / warning / success tokens. */
function markerGradient(projectedPct: number): string {
  // hsl values taken from the Evo design-system CSS variables:
  //   destructive → red-ish   warning → amber   success → green
  const colour =
    projectedPct > 150
      ? 'var(--color-destructive, #ef4444)'
      : projectedPct > 100
      ? 'var(--color-warning-500, #f59e0b)'
      : 'var(--color-success-500, #22c55e)';
  return `repeating-linear-gradient(to bottom, ${colour} 0px, ${colour} 3px, transparent 3px, transparent 6px)`;
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function PurchasesKpiBar({ requests, costBudgetTotal }: PurchasesKpiBarProps) {
  // ── Derived metrics ──────────────────────────────────────────────────────
  const m = useMemo(() => {
    const inFlightStatuses = new Set([
      'Pending',
      'Awaiting Approval',
      'Reopened',
      'Redirected',
    ]);
    const rejectedStatuses = new Set(['Rejected', 'Cancelled']);

    const committedList = requests.filter(
      (r) => r.status === 'Posted' && !r.isCommitmentRelieved,
    );
    const invoicedList = requests.filter(
      (r) => r.status === 'Posted' && r.isCommitmentRelieved === true,
    );
    const inFlightList = requests.filter((r) => inFlightStatuses.has(r.status));
    const rejectedList = requests.filter((r) => rejectedStatuses.has(r.status));

    const committedValue = committedList.reduce((s, r) => s + r.amount, 0);
    const invoicedValue  = invoicedList.reduce((s, r) => s + r.amount, 0);
    const inFlightValue  = inFlightList.reduce((s, r) => s + r.amount, 0);
    const rejectedValue  = rejectedList.reduce((s, r) => s + r.amount, 0);

    // Oldest in-flight date
    let oldestPendingDate: string | null = null;
    if (inFlightList.length > 0) {
      oldestPendingDate = inFlightList.reduce<string>((oldest, r) => {
        return !oldest || new Date(r.date).getTime() < new Date(oldest).getTime()
          ? r.date
          : oldest;
      }, '');
    }

    // Most-recent rejection reference (by date)
    let mostRecentRejectionRef: string | null = null;
    if (rejectedList.length > 0) {
      const latest = rejectedList.reduce((a, b) =>
        new Date(a.date).getTime() >= new Date(b.date).getTime() ? a : b,
      );
      mostRecentRejectionRef = latest.requestId;
    }

    return {
      committedValue,
      committedCount: committedList.length,
      invoicedValue,
      inFlightValue,
      inFlightCount: inFlightList.length,
      rejectedValue,
      rejectedCount: rejectedList.length,
      projectedValue: committedValue + inFlightValue,
      oldestPendingDate,
      mostRecentRejectionRef,
    };
  }, [requests]);

  // ── Budget progress calculations ─────────────────────────────────────────
  const hasBudget = costBudgetTotal != null && costBudgetTotal > 0;
  const committedPct = hasBudget ? (m.committedValue / (costBudgetTotal as number)) * 100 : 0;
  const projectedPct = hasBudget ? (m.projectedValue / (costBudgetTotal as number)) * 100 : 0;
  const showMarker   = hasBudget && m.inFlightValue > 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        {/* ── 1. Committed Cost ──────────────────────────────────────────── */}
        <KpiShell
          accent="teal"
          icon={TrendingUp}
          title="Committed Cost"
          value={formatCurrency(m.committedValue)}
          description={
            m.committedCount === 1
              ? '1 approved / posted request'
              : `${m.committedCount} approved / posted requests`
          }
        >
          {hasBudget && (
            <div className="pt-1.5">
              {/* Progress track */}
              <div className="relative h-2 rounded-full bg-muted">
                {/* Committed fill — uses success token to match the card accent */}
                <div
                  className="absolute top-0 left-0 h-full rounded-full bg-success-500 transition-all"
                  style={{ width: `${Math.min(committedPct, 100)}%` }}
                />

                {/* Projected marker (dashed vertical line) — only when there are in-flight PRs */}
                {showMarker && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/*
                       * The marker is a 2-px wide strip positioned at the projected %,
                       * capped at 100 % so it stays within the track.
                       * A repeating gradient fakes the dashed stroke.
                       */}
                      <div
                        className="absolute top-0 h-full w-0.5 cursor-pointer"
                        style={{
                          left: `${Math.min(projectedPct, 100)}%`,
                          backgroundImage: markerGradient(projectedPct),
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Committed: {formatCurrency(m.committedValue)}
                      {' · '}
                      In-flight: {formatCurrency(m.inFlightValue)}
                      {' · '}
                      Projected: {formatCurrency(m.projectedValue)} ({projectedPct.toFixed(0)}%)
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Budget usage label */}
              <p className="text-[10px] text-muted-foreground mt-1">
                {committedPct.toFixed(0)}% of {formatCurrency(costBudgetTotal)} budget
                {showMarker && (
                  <span
                    className={cn(
                      'ml-1 font-medium',
                      projectedPct > 150
                        ? 'text-destructive'
                        : projectedPct > 100
                        ? 'text-warning-600'
                        : 'text-success-600',
                    )}
                  >
                    · {projectedPct.toFixed(0)}% projected
                  </span>
                )}
              </p>
            </div>
          )}
        </KpiShell>

        {/* ── 2. Invoiced / Actual Spend ─────────────────────────────────── */}
        <KpiShell
          accent="indigo"
          icon={Receipt}
          title="Invoiced / Actual Spend"
          value={formatCurrency(m.invoicedValue)}
          description={`Outstanding PO: ${formatCurrency(m.committedValue)}`}
        />

        {/* ── 3. Awaiting Approval ──────────────────────────────────────── */}
        <KpiShell
          accent="amber"
          icon={Clock}
          title="Awaiting Approval"
          value={m.inFlightCount}
          description={
            m.inFlightCount > 0
              ? `${formatCurrency(m.inFlightValue)} · Oldest: ${formatDate(m.oldestPendingDate)}`
              : 'No in-flight requests'
          }
        />

        {/* ── 4. Rejected / Cancelled ───────────────────────────────────── */}
        <KpiShell
          accent="red"
          icon={XCircle}
          title="Rejected / Cancelled"
          value={m.rejectedCount}
          description={
            m.rejectedCount > 0 && m.mostRecentRejectionRef
              ? `${formatCurrency(m.rejectedValue)} · Ref: ${m.mostRecentRejectionRef}`
              : m.rejectedCount > 0
              ? formatCurrency(m.rejectedValue)
              : 'None'
          }
        />

      </div>
    </TooltipProvider>
  );
}
